import React, { useEffect, useState } from 'react';
import { Modal, Button, Select, Tag, Space, Typography, Divider, message, Alert } from 'antd';
import { DeleteOutlined, SafetyOutlined } from '@ant-design/icons';
import { authClient } from '../../../providers/axios';
import type { RoleAssignment } from '../utils';
import { getRoleScopeColor } from '../utils';

export type { RoleAssignment };

const { Text } = Typography;

interface AvailableRole {
  id: string;
  _id?: string;
  name: string;
  description?: string;
  scope?: 'internal' | 'customer';
  permissions?: string[];
}

interface RoleAssignmentEditorProps {
  userId: string;
  currentAssignments: RoleAssignment[];
  open: boolean;
  onClose: () => void;
  onSaved: (updated: RoleAssignment[]) => void;
}

export const RoleAssignmentEditor: React.FC<RoleAssignmentEditorProps> = ({
  userId,
  currentAssignments,
  open,
  onClose,
  onSaved,
}) => {
  const [assignments, setAssignments] = useState<RoleAssignment[]>([]);
  const [availableRoles, setAvailableRoles] = useState<AvailableRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Fetch available roles when modal opens
  useEffect(() => {
    if (open) {
      setAssignments(
        currentAssignments.map((a) => ({
          role: a.role,
          customerScope: a.customerScope != null ? [...a.customerScope] : null,
        })),
      );
      fetchRoles();
    }
  }, [open, currentAssignments]);

  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const response = await authClient.get('/roles');
      const roles = response.data?.roles || response.data?.data || response.data || [];
      setAvailableRoles(Array.isArray(roles) ? roles : []);
    } catch (err) {
      message.error('Failed to load available roles');
    } finally {
      setRolesLoading(false);
    }
  };

  const getRoleInfo = (roleName: string): AvailableRole | undefined => {
    return availableRoles.find((r) => r.name === roleName);
  };

  const isCustomerScoped = (roleName: string): boolean => {
    const role = getRoleInfo(roleName);
    return role?.scope === 'customer';
  };

  const getAssignedRoleNames = (): string[] => {
    return assignments.map((a) => a.role);
  };

  const handleAddRole = (roleName: string) => {
    if (getAssignedRoleNames().includes(roleName)) {
      message.warning(`Role "${roleName}" is already assigned`);
      return;
    }
    const newAssignment: RoleAssignment = { role: roleName, customerScope: null };
    if (isCustomerScoped(roleName)) {
      newAssignment.customerScope = [];
    }
    setAssignments([...assignments, newAssignment]);
    setValidationErrors([]);
  };

  const handleRemoveRole = (index: number) => {
    setAssignments(assignments.filter((_, i) => i !== index));
    setValidationErrors([]);
  };

  const handleCustomerScopeChange = (index: number, values: string[]) => {
    const updated = [...assignments];
    updated[index] = { ...updated[index], customerScope: values };
    setAssignments(updated);
    setValidationErrors([]);
  };

  const validate = (): string[] => {
    const errors: string[] = [];
    const seen = new Set<string>();

    assignments.forEach((a) => {
      if (seen.has(a.role)) {
        errors.push(`Duplicate role: "${a.role}"`);
      }
      seen.add(a.role);

      if (isCustomerScoped(a.role) && (!a.customerScope || a.customerScope.length === 0)) {
        errors.push(
          `Customer-scoped role "${a.role}" requires at least one company in customerScope`,
        );
      }
    });

    return errors;
  };

  const handleSave = async () => {
    if (!userId) {
      message.error('Cannot save: user ID is missing');
      return;
    }
    const errors = validate();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setLoading(true);
    try {
      // Clean up: remove customerScope from internal roles
      // Guard: only strip customerScope if roles have loaded — otherwise we can't
      // reliably determine scope and would silently remove valid data.
      // Only strip customerScope from internal roles if roles have loaded.
      // If availableRoles is empty (load failed/pending), preserve existing
      // scope data rather than silently stripping it.
      const cleanedAssignments: RoleAssignment[] = assignments.map((a) => {
        if (availableRoles.length > 0 && !isCustomerScoped(a.role)) {
          return { role: a.role, customerScope: null };
        }
        return { role: a.role, customerScope: a.customerScope ?? null };
      });

      await authClient.patch(`/users/${userId}`, {
        roleAssignments: cleanedAssignments,
      });

      message.success('Role assignments updated successfully');
      onSaved(cleanedAssignments);
      onClose();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message ||
        (err as { message?: string })?.message ||
        'Unknown error';
      message.error(`Failed to update role assignments: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setValidationErrors([]);
    onClose();
  };

  return (
    <Modal
      title="Edit Role Assignments"
      open={open}
      onCancel={handleCancel}
      width={640}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={loading} onClick={handleSave}>
          Save
        </Button>,
      ]}
    >
      {validationErrors.length > 0 && (
        <Alert
          type="error"
          message="Validation Errors"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          }
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setValidationErrors([])}
        />
      )}

      {/* Add role selector */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>Add a role:</Text>
        <Select
          style={{ width: '100%', marginTop: 8 }}
          placeholder="Select a role to add..."
          loading={rolesLoading}
          value={undefined}
          onSelect={(value) => {
            if (value) handleAddRole(value);
          }}
          showSearch
          filterOption={(input, option) =>
            (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
          }
          options={availableRoles
            .filter((r) => !getAssignedRoleNames().includes(r.name))
            .map((r) => ({
              label: `${r.name}${r.scope === 'customer' ? ' (customer-scoped)' : ''}${r.scope === 'internal' ? ' (internal)' : ''}`,
              value: r.name,
            }))}
        />
      </div>

      <Divider />

      {/* Current assignments */}
      {assignments.length === 0 ? (
        <Text type="secondary">No roles assigned. Use the selector above to add roles.</Text>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {assignments.map((assignment, index) => {
            const roleInfo = getRoleInfo(assignment.role);
            const isCustomer = isCustomerScoped(assignment.role);

            return (
              <div
                key={`${assignment.role}-${index}`}
                style={{
                  padding: 12,
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  background: roleInfo?.scope === 'internal' ? '#fafafa' : '#fff',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: isCustomer ? 8 : 0,
                  }}
                >
                  <Space>
                    <SafetyOutlined />
                    <Text strong>{assignment.role}</Text>
                    {roleInfo?.scope && (
                      <Tag color={getRoleScopeColor(roleInfo.scope)}>{roleInfo.scope}</Tag>
                    )}
                    {roleInfo?.description && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {roleInfo.description}
                      </Text>
                    )}
                  </Space>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveRole(index)}
                    size="small"
                  />
                </div>

                {isCustomer && (
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Customer Scope (company IDs):
                    </Text>
                    <Select
                      mode="tags"
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="Type company IDs and press Enter..."
                      value={assignment.customerScope || []}
                      onChange={(values) => handleCustomerScopeChange(index, values)}
                      tokenSeparators={[',']}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </Space>
      )}
    </Modal>
  );
};
